import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import TrackVisibility from 'react-on-screen';
import CloseIcon from '../../../../public/static/icons/close-header-main.svg';
import Slider from '../../../01_atoms/Slider';
import * as breakpoints from '../../../../utils/breakpoint';
import {
  behaviorSettingsProps,
  generateClassNameByBehaviorSettings,
} from '../../../../utils/behaviorSettings';

import styles from './index.module.scss';

/**
 * Use **canvas** to see animation for the block.
 */
const BBFourTileImpact = ({ variant = 'green', items, behaviorSettings = null, uuid = null }) => {
  const [openedTile, setOpenedTile] = useState(-1);
  const [sliderOpen, setSliderOpen] = useState(false);

  const sliderSettings = {
    dots: true,
    infinite: true,
    arrows: false,
    speed: 100,
    slidesToScroll: 1,
  };

  const sliderRef = useRef(null);

  useEffect(() => {
    const checkIfClickedOutside = (event) => {
      if (
        openedTile >= 0 &&
        !document.getElementById(`bb-four-tile-impact-items-${uuid}`).contains(event.target)
      ) {
        setOpenedTile(-1);
      }
    };
    document.addEventListener('mousedown', checkIfClickedOutside);

    const closeOnEsc = (event) => {
      if (openedTile >= 0 && event.keyCode === 27) {
        setOpenedTile(-1);
      }
    };
    document.addEventListener('keydown', closeOnEsc);

    return () => {
      // Cleanup the event listener
      document.removeEventListener('mousedown', checkIfClickedOutside);
      document.removeEventListener('keydown', closeOnEsc);
    };
  }, [openedTile, uuid]);

  const classes = [
    'bb',
    'bb-four-tile-impact',
    styles['bb-four-tile-impact'],
    styles[`bb-four-tile-impact-variant-${variant}`],
    sliderOpen ? styles['slick-slider-opened'] : '',
    generateClassNameByBehaviorSettings(behaviorSettings),
  ];

  return (
    <TrackVisibility once>
      {({ isVisible }) => (
        <div
          className={`${classes.join(' ')} ${
            isVisible ? styles['bb-four-tile-impact-heading-show'] : ''
          }`}
          id={uuid}
        >
          <div className="container">
            {items && !!items.length && (
              <>
                <Slider ref={sliderRef} {...sliderSettings}>
                  {items.map((item, index) => {
                    return (
                      <div
                        key={index}
                        tabIndex="0"
                        className={`${styles['bb-four-tile-impact-slide']} ${
                          styles[`bb-four-tile-impact-slide-${index}`]
                        }`}
                      >
                        <div
                          style={{
                            backgroundImage: `url("${item.image.url}")`,
                          }}
                          title={item.image.alt}
                          className={styles['bb-four-tile-impact-item-background']}
                        />
                        <div className={styles['bb-four-tile-impact-heading']}>
                          <h3>{item.title}</h3>
                        </div>
                        <div
                          className={`${styles['bb-four-tile-impact-text']} ${
                            openedTile === index ? styles['bb-four-tile-impact-opened'] : ''
                          }`}
                        >
                          <button
                            className={`${styles['bb-four-tile-impact-close-icon']} bb-four-tile-impact-close-icon`}
                            role="button"
                            aria-label="Close"
                            onClick={() => setOpenedTile(-1)}
                          >
                            <CloseIcon />
                          </button>
                          <p aria-live="assertive" aria-atomic="true">
                            {item.text}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </Slider>
                {sliderOpen !== false && (
                  <button
                    className={`${styles['bb-four-tile-impact-close-icon']} ${styles['bb-four-tile-impact-close-slider']} bb-four-tile-impact-close-icon`}
                    role="button"
                    aria-label="Close"
                    onClick={() => setSliderOpen(false)}
                  >
                    <i />
                  </button>
                )}
                <button
                  className={`${styles['bb-four-tile-impact-slider-prevnext']} ${styles['bb-four-tile-impact-slider-prev']}`}
                  role="button"
                  aria-label="Previous"
                  onClick={() => sliderRef.current.slickPrev()}
                >
                  <i />
                </button>
                <button
                  className={`${styles['bb-four-tile-impact-slider-prevnext']} ${styles['bb-four-tile-impact-slider-next']}`}
                  role="button"
                  aria-label="Next"
                  onClick={() => sliderRef.current.slickNext()}
                >
                  <i />
                </button>
                <ul
                  className={styles['bb-four-tile-impact-items']}
                  id={`bb-four-tile-impact-items-${uuid}`}
                  role="list"
                >
                  {items.map((item, index) => {
                    return (
                      <li
                        key={index}
                        tabIndex="0"
                        className={`${styles['bb-four-tile-impact-item']} bb-four-tile-impact-item`}
                        role="listitem"
                      >
                        <div
                          role="button"
                          aria-haspopup="true"
                          aria-label={item.title}
                          className={styles['bb-four-tile-impact-item-button']}
                          onClick={() => {
                            if (breakpoints.isUp('lg')) {
                              if (openedTile !== index) {
                                setOpenedTile(index);
                              }
                            } else {
                              sliderRef.current.slickGoTo(index);
                              setSliderOpen(true);
                            }
                          }}
                          onKeyPress={(event) => {
                            // On enter.
                            if (event.charCode === 13) {
                              setOpenedTile(index);
                            }
                            const closeIcon = event.target.getElementsByClassName(
                              'bb-four-tile-impact-close-icon',
                            );
                            if (closeIcon.length) {
                              closeIcon[0].focus();
                            }
                          }}
                        >
                          <div
                            style={{
                              backgroundImage: `url("${item.image.url}")`,
                            }}
                            title={item.image.alt}
                            className={styles['bb-four-tile-impact-item-background']}
                            role="img"
                          />
                          <div className={styles['bb-four-tile-impact-heading']}>
                            <h3>{item.title}</h3>
                          </div>
                        </div>
                        {openedTile === index && (
                          <div
                            className={`${styles['bb-four-tile-impact-text']} ${
                              openedTile === index ? styles['bb-four-tile-impact-opened'] : ''
                            }`}
                          >
                            <button
                              className={`${styles['bb-four-tile-impact-close-icon']} bb-four-tile-impact-close-icon`}
                              role="button"
                              aria-label="Close"
                              tabIndex={openedTile === index ? '' : '-1'}
                              onClick={() => setOpenedTile(-1)}
                            >
                              <CloseIcon />
                            </button>
                            <p aria-live="assertive" aria-atomic="true">
                              {item.text}
                            </p>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </div>
        </div>
      )}
    </TrackVisibility>
  );
};

BBFourTileImpact.propTypes = {
  variant: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      text: PropTypes.string,
      image: PropTypes.shape({
        url: PropTypes.string,
        alt: PropTypes.string,
      }),
    }),
  ).isRequired,
  behaviorSettings: behaviorSettingsProps,
  uuid: PropTypes.string,
};

export default BBFourTileImpact;
